import { TrophyIcon } from "@/assets/images/svgIcons/trophyIcon"
import { Badge } from "@/components/ui/baseUi/badge"
import { Button } from "@/components/ui/baseUi/button"
import { InputContainer } from "@/components/ui/forms/inputContainer"
import { TextAreaInput } from "@/components/ui/forms/textareaInput"
import { Switch } from "@/components/ui/baseUi/switch"
import { Text } from "@/components/ui/baseUi/text"
import { useLanguage } from "@/modules/language/hooks/useLanguage"
import { cn } from "@/modules/lib/utils"
import { useProgram } from "@/modules/programs/hooks/usePrograms"
import { ScoreSetCompletionInput } from "@/modules/scores/scoreForms/components/scoreSetCompletionInput"
import { useScoreFormik } from "@/modules/scores/scoreForms/hooks/useScoreFormFormik"
import { createScoreFormModel } from "@/modules/scores/scoreForms/models/scoreFormModel"
import type { ProgramInfiniteSessionEntity } from "@/modules/sessions/entities/programInfiniteSessionEntity"
import type { ProgramOnOffSessionEntity } from "@/modules/sessions/entities/programOnOffSessionEntity"
import type { SessionBlockExerciseEntity } from "@/modules/sessions/entities/sessionBlockExerciseEntity"
import { useUnit } from "@/modules/utils/hooks/useUnit"
import { word } from "@modules/core/utils/i18n"
import { getFormErrorMessage } from "@modules/formik/formikUtils"
import { MediaUpload } from "@modules/ui/components/fileUpload/mediaUpload"
import get from "lodash/get"
import { LoaderCircle, VideoIcon } from "lucide-react"

type ScoreFormProps = {
  exercise: SessionBlockExerciseEntity
  parentSession: ProgramInfiniteSessionEntity | ProgramOnOffSessionEntity
  className?: string
} & React.ComponentProps<"div">

export function ScoreForm({ exercise, parentSession, className, ...props }: ScoreFormProps) {
  const { exerciseScoreFormik, isSubmitting } = useScoreFormik(parentSession, exercise)
  const { data: program } = useProgram(parentSession.programId)
  const unit = useUnit()
  const language = useLanguage()
  const isUploadingAMedia = exerciseScoreFormik.values.medias.some((media) => media.isUploading)

  const {
    formTitle,
    formSubtitle,
    displayedScore,
    displayIsScoreMediaRequired,
    difficultyOptions,
    displayScoreTotal,
    resultTypeCaption,
    displayTotalMissingReps,
    missingRepsCount,
  } = createScoreFormModel(program, exercise, exerciseScoreFormik.values, unit, language)

  return (
    <div className={cn("flex flex-col gap-6 px-5 pt-18 pb-6", className)} {...props}>
      {/* Score form title*/}
      <div className="flex flex-col gap-2">
        <Text fontStyle="TL">{formTitle}</Text>
        <Text fontStyle="BS">{formSubtitle}</Text>
      </div>

      {/* Media is requested (not required) */}
      {displayIsScoreMediaRequired && (
        <div className="flex flex-row items-center justify-start gap-2 rounded-2xl bg-surface-foreground px-4 py-2.5">
          <VideoIcon color="currentColor" className="text-textError" />
          <Text fontStyle="BS">{word("program.planning.create.exercise.media_requirement.requested")}</Text>
        </div>
      )}

      {/* Score difficulty form */}
      <div className={`rounded-2xl bg-surface-foreground p-4`}>
        <InputContainer
          label={word("exercise.score.difficulty")}
          isRequiredInput={true}
          error={getFormErrorMessage("difficulty", exerciseScoreFormik)}
        >
          <div className="mt-2 flex flex-row flex-wrap items-center gap-2">
            {difficultyOptions.map((difficulty) => (
              <Badge
                key={difficulty.value}
                size="lg"
                variant={
                  exerciseScoreFormik.values.difficulty === difficulty.value ? "primaryIntense" : "neutralSubtle"
                }
                onClick={() => {
                  void exerciseScoreFormik.setFieldValue("difficulty", difficulty.value)
                }}
                className="cursor-pointer"
                role="button"
                render={
                  <Text fontStyle="LM" className="text-textDefault-body">
                    {difficulty.label}
                  </Text>
                }
              />
            ))}
          </div>
        </InputContainer>
      </div>

      {/* Score(s) form */}
      <div className={`rounded-2xl bg-surface-foreground p-4`}>
        <Text fontStyle="LS" className="text-textDefault-body uppercase">
          {word("exercise.score.form.scoreInput.label")}
          <Text fontStyle="LL" className="text-textError">
            {" *"}
          </Text>
        </Text>

        <div className="flex flex-col gap-3">
          {exerciseScoreFormik.values.scoreSetCompletions.map((_scoreSetCompletion, index) => (
            <div key={index} className="flex flex-col gap-0.5">
              {exerciseScoreFormik.values.scoreSetCompletions.length > 1 && (
                <Text fontStyle="TS" className="font-semibold">
                  {word("session.score.form.setNumber")} {index + 1}
                </Text>
              )}
              <ScoreSetCompletionInput
                formik={exerciseScoreFormik}
                scoreSetCompletionIndex={index}
                exercise={exercise}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Score total */}
      {displayScoreTotal && (
        <div className="flex h-10 flex-row items-center justify-between rounded-4xl bg-feedback-positive-subtle px-4">
          <div className="flex flex-row items-center gap-1">
            <Text fontStyle="BM" className="text-feedback-positive-intense">
              {resultTypeCaption ?? ""}
            </Text>
            <TrophyIcon color="var(--feedback-positive-intense)" size={16} />
          </div>

          {displayedScore !== "" && (
            <Text fontStyle="BM" className="font-semibold text-feedback-positive-intense">
              {displayedScore}
            </Text>
          )}
        </div>
      )}

      {/* Total missing reps */}
      {displayTotalMissingReps && (
        <div className="flex h-10 flex-row items-center justify-between rounded-4xl border border-borderDefault px-4">
          <Text fontStyle="BM">
            {word("session.score.form.timeCapExceeded.missingReps", {
              count: missingRepsCount,
            })}
          </Text>
          <Text fontStyle="BM" className="font-semibold">
            {missingRepsCount}
          </Text>
        </div>
      )}

      {/* PR toggle */}
      <div className="flex flex-row items-center justify-between px-5">
        <Text fontStyle="BS">{word("session.score.form.personalRecord.label")}</Text>
        <Switch
          checked={exerciseScoreFormik.values.personalRecord}
          onCheckedChange={() =>
            void exerciseScoreFormik.setFieldValue("personalRecord", !exerciseScoreFormik.values.personalRecord)
          }
        />
      </div>

      {/* Comment input */}
      <TextAreaInput
        label={word("session.scoreboard.card.comment")}
        name="note"
        isRequiredInput={exerciseScoreFormik.values.scoreSetCompletions.length === 0}
        getFieldProps={exerciseScoreFormik.getFieldProps}
        setFieldValue={exerciseScoreFormik.setFieldValue}
        error={getFormErrorMessage("note", exerciseScoreFormik)}
        textareaProps={{
          placeholder: word("exercise.score.addComment"),
          className: "h-35 bg-surface-background field-sizing-fixed",
          rows: 5,
        }}
      />

      {/* Visibility switch */}
      <div className="gap-4 px-2">
        <Text className="my-1 uppercase" fontStyle="LS">
          {word("session.comment.visibility.label")}
        </Text>
        <div className="mt-2 flex w-full flex-row items-center justify-between gap-2">
          <Text fontStyle="BS" className="flex-1">
            {word("session.comment.visibility.description")}
          </Text>
          <Switch
            checked={exerciseScoreFormik.values.noteAndMediaIsVisible}
            onCheckedChange={() =>
              void exerciseScoreFormik.setFieldValue(
                "noteAndMediaIsVisible",
                !exerciseScoreFormik.values.noteAndMediaIsVisible,
              )
            }
          />
        </div>
      </div>

      {/* Media upload */}
      <MediaUpload
        className="my-2"
        type="mediaItem"
        mediaList={exerciseScoreFormik.values.medias}
        updateMediaList={(mediaList) => {
          void exerciseScoreFormik.setFieldValue("medias", mediaList)
        }}
        updateMediaItem={(mediaItem, index) => {
          void exerciseScoreFormik.setFieldValue(`medias[${index}]`, mediaItem)
        }}
      />

      {typeof get(exerciseScoreFormik.errors, "medias") === "string" &&
        getFormErrorMessage("medias", exerciseScoreFormik)}

      {/* Save button */}
      <Button
        variant="primary"
        size="lg"
        onClick={() => void exerciseScoreFormik.submitForm()}
        disabled={isUploadingAMedia || isSubmitting || Object.keys(exerciseScoreFormik.errors).length > 0}
        isLoading={isSubmitting}
      >
        {isSubmitting && <LoaderCircle className="animate-spin" />}
        {word("global.save")}
      </Button>
    </div>
  )
}
